import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Lock, Crown, Check } from "lucide-react";

// Elegant "locked preview": shows the real UI dimmed behind a premium CTA. Never a blank wall.
export function PremiumGate({ feature, children, preview, title, description, benefits = [], testid }) {
  const { entitlements } = useAuth();
  const navigate = useNavigate();
  const f = entitlements?.features?.[feature];
  const expired = entitlements?.premium?.status === "expired";

  if (f?.allowed) return children;

  const msg = description || f?.upgrade || "Cette fonctionnalité est réservée aux abonnés Premium.";
  return (
    <div className="relative" data-testid={testid || `gate-${feature}`}>
      <div className="pointer-events-none select-none opacity-40 blur-[1.5px] max-h-[520px] overflow-hidden" aria-hidden>
        {preview || children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="max-w-md w-full card-lux p-7 text-center bg-white/95 backdrop-blur shadow-2xl">
          <div className="w-12 h-12 rounded-2xl bg-champagne/15 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-5 h-5 text-champagne" />
          </div>
          <div className="eyebrow mb-1 text-champagne">Fonction Premium</div>
          <h3 className="font-serif text-2xl text-stone-900">{title || f?.label}</h3>
          <p className="text-sm text-stone-500 mt-2">{expired
            ? "Votre abonnement Premium a expiré. Vos données sont conservées — réactivez pour reprendre là où vous vous étiez arrêté."
            : msg}</p>
          {benefits.length > 0 && (
            <ul className="text-sm text-stone-600 mt-4 space-y-1.5 text-left inline-block">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-sage mt-0.5 shrink-0" /> {b}</li>
              ))}
            </ul>
          )}
          <Button onClick={() => navigate("/app/premium")} data-testid="gate-upgrade-btn"
            className="rounded-full mt-6 w-full sm:w-auto px-8">
            <Crown className="w-4 h-4 mr-2" /> {expired ? "Réactiver Premium" : "Passer Premium"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Compact inline banner (dashboard, headers) inviting upgrade — hidden when already Premium.
export function PremiumBanner({ title, text }) {
  const { premium } = useAuth();
  const navigate = useNavigate();
  if (premium?.active) return null;
  return (
    <div className="rounded-2xl p-5 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
      style={{ background: "linear-gradient(120deg,#1c1b1a,#3a332a)" }} data-testid="premium-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-champagne/20 flex items-center justify-center shrink-0">
          <Crown className="w-5 h-5 text-champagne" /></div>
        <div>
          <div className="font-serif text-lg text-white">{title || "Passez à Premium et préparez votre mariage sans limites"}</div>
          <div className="text-sm text-white/70 mt-0.5">{text || "Gazette des mariés, site de mariage, exports HD et bien plus."}</div>
        </div>
      </div>
      <Button onClick={() => navigate("/app/premium")} data-testid="banner-upgrade-btn"
        className="rounded-full bg-champagne text-stone-900 hover:bg-champagne/90 shrink-0">Découvrir Premium</Button>
    </div>
  );
}

// Small badge for locked nav/section labels.
export function PremiumBadge() {
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-champagne/15 text-champagne">
    <Crown className="w-2.5 h-2.5" /> Premium</span>;
}
